import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/pages";
import { toast } from "sonner";
import { Scissors, Archive, Truck, SignOut, ArrowLeft } from "@phosphor-icons/react";

/**
 * Mobile-friendly shop-floor Ops View for entering Cutting, Packing & Dispatch on a phone.
 * Large tap targets, minimal chrome, no side navigation. Each tab shows today's entries live.
 */
export default function OpsView() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("cutting");
  const [products, setProducts] = useState([]);
  const [parties, setParties] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/parties").then((r) => setParties(r.data));
  }, []);

  const canCut = canAccess(user, "cutting");
  const canPack = canAccess(user, "packing");
  const canDispatch = canAccess(user, "dispatch");

  useEffect(() => {
    if (!canCut && canPack) setTab("packing");
    else if (!canCut && !canPack && canDispatch) setTab("dispatch");
  }, [canCut, canPack, canDispatch]);

  if (!canCut && !canPack && !canDispatch) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-background">
        <div className="text-center">
          <div className="label-caps text-destructive">Access Denied</div>
          <div className="font-display font-black text-2xl mt-2">No shop-floor access</div>
          <div className="mt-6">
            <button
              onClick={() => nav("/")}
              className="h-12 px-6 font-mono text-sm bg-foreground text-background"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* HEADER */}
      <header className="bg-[#0a1330] text-white sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => nav("/")}
            data-testid="ops-back"
            className="w-11 h-11 flex items-center justify-center border border-white/30 hover:bg-white/10"
            aria-label="Back to full dashboard"
          >
            <ArrowLeft size={20} weight="bold" />
          </button>
          <div className="text-center">
            <div className="label-caps text-white/50">Shop Floor</div>
            <div className="font-display font-black text-lg leading-none">SLKC · OPS</div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="ops-logout"
            className="w-11 h-11 flex items-center justify-center border border-white/30 hover:bg-white/10"
            aria-label="Logout"
          >
            <SignOut size={20} weight="bold" />
          </button>
        </div>
        <div className="grid grid-cols-3 border-t border-white/20">
          {canCut && (
            <button
              onClick={() => setTab("cutting")}
              data-testid="ops-tab-cutting"
              className={`h-14 flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase font-bold ${tab === "cutting" ? "bg-white text-[#0a1330]" : "text-white/70"}`}
            >
              <Scissors size={18} weight="bold" /> Cut
            </button>
          )}
          {canPack && (
            <button
              onClick={() => setTab("packing")}
              data-testid="ops-tab-packing"
              className={`h-14 flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase font-bold ${tab === "packing" ? "bg-white text-[#0a1330]" : "text-white/70"}`}
            >
              <Archive size={18} weight="bold" /> Pack
            </button>
          )}
          {canDispatch && (
            <button
              onClick={() => setTab("dispatch")}
              data-testid="ops-tab-dispatch"
              className={`h-14 flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase font-bold ${tab === "dispatch" ? "bg-white text-[#0a1330]" : "text-white/70"}`}
            >
              <Truck size={18} weight="bold" /> Ship
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 p-4 pb-24 max-w-lg mx-auto w-full">
        <div className="mb-3 text-xs font-mono text-muted-foreground">
          Signed in as <b className="text-foreground">{user?.name}</b>
        </div>
        {tab === "cutting" && <CuttingOps products={products} />}
        {tab === "packing" && <PackingOps products={products} />}
        {tab === "dispatch" && <DispatchOps products={products} parties={parties} />}
      </main>
    </div>
  );
}

function BigInput(props) {
  return (
    <input
      {...props}
      className={`w-full h-14 px-4 bg-white border-2 border-border focus:border-foreground focus:outline-none font-mono text-lg ${props.className || ""}`}
    />
  );
}

function BigSelect({ children, ...rest }) {
  return (
    <select
      {...rest}
      className={`w-full h-14 px-3 bg-white border-2 border-border focus:border-foreground focus:outline-none font-mono text-base ${rest.className || ""}`}
    >
      {children}
    </select>
  );
}

function BigLabel({ children }) {
  return <div className="font-mono text-[11px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">{children}</div>;
}

function TodayList({ testid, rows, columns, emptyText }) {
  const todayStr = today();
  const filtered = rows.filter((r) => r.date === todayStr);
  return (
    <div className="mt-6 border-2 border-border bg-white" data-testid={testid}>
      <div className="px-3 py-2 border-b-2 border-border font-mono text-[11px] tracking-[0.2em] uppercase font-bold flex justify-between">
        <span>Today · {filtered.length} entr{filtered.length === 1 ? "y" : "ies"}</span>
        <span className="text-muted-foreground">{todayStr}</span>
      </div>
      {filtered.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground font-mono">{emptyText}</div>
      ) : (
        <ul className="divide-y divide-border">
          {filtered.slice(0, 20).map((r, i) => (
            <li key={r.id || i} className="px-3 py-2 text-sm">
              {columns(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CuttingOps({ products }) {
  const [f, setF] = useState({ date: today(), product_id: "", fabric_kg: "", pcs_out: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);

  const reload = () => api.get("/cutting").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const ratio = f.fabric_kg && f.pcs_out ? (Number(f.pcs_out) / Number(f.fabric_kg)).toFixed(2) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.product_id) return toast.error("Select a product");
    setSaving(true);
    try {
      await api.post("/cutting", {
        ...f, fabric_kg: Number(f.fabric_kg), pcs_out: parseInt(f.pcs_out),
      });
      toast.success(`Saved · ${f.pcs_out} pcs cut`);
      setF({ date: today(), product_id: "", fabric_kg: "", pcs_out: "", notes: "" });
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form onSubmit={submit} className="space-y-5" data-testid="ops-cutting-form">
        <div>
          <BigLabel>Date</BigLabel>
          <BigInput type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        </div>
        <div>
          <BigLabel>Product</BigLabel>
          <BigSelect required value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} data-testid="ops-cutting-product">
            <option value="">Choose a product…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.style_name} · {p.size} · {p.color}</option>
            ))}
          </BigSelect>
        </div>
        <div>
          <BigLabel>Fabric Used (Kg)</BigLabel>
          <BigInput type="number" inputMode="decimal" step="0.01" required value={f.fabric_kg} onChange={(e) => setF({ ...f, fabric_kg: e.target.value })} data-testid="ops-cutting-kg" />
        </div>
        <div>
          <BigLabel>Pieces Cut</BigLabel>
          <BigInput type="number" inputMode="numeric" required value={f.pcs_out} onChange={(e) => setF({ ...f, pcs_out: e.target.value })} data-testid="ops-cutting-pcs" />
        </div>
        <div className="bg-secondary border-2 border-border p-4">
          <BigLabel>Conversion</BigLabel>
          <div className="font-display font-black text-3xl">{ratio} <span className="text-sm text-muted-foreground">pcs/kg</span></div>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="ops-cutting-submit"
          className="w-full h-16 bg-foreground text-background font-mono text-sm tracking-[0.2em] uppercase font-bold hover:-translate-y-[1px] transition-transform disabled:opacity-50"
        >
          {saving ? "Saving…" : "✓ Save Cutting"}
        </button>
      </form>
      <TodayList
        testid="ops-cutting-today"
        rows={rows}
        emptyText="No cutting yet today"
        columns={(r) => (
          <div className="flex justify-between items-baseline gap-2">
            <span className="truncate">{r.product_label}</span>
            <span className="font-mono tabular whitespace-nowrap">
              <b>{r.pcs_out}</b> pcs · {r.fabric_kg}kg
            </span>
          </div>
        )}
      />
    </>
  );
}

function PackingOps({ products }) {
  const [f, setF] = useState({ date: today(), product_id: "", pcs: "", labels_used: "", stickers_used: "", boxes_used: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);

  const reload = () => api.get("/packing").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const setDefault = (val) => setF((p) => ({
    ...p, pcs: val,
    labels_used: p.labels_used || val,
    stickers_used: p.stickers_used || val,
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.product_id) return toast.error("Select a product");
    setSaving(true);
    try {
      await api.post("/packing", {
        ...f,
        pcs: parseInt(f.pcs),
        labels_used: parseInt(f.labels_used || 0),
        stickers_used: parseInt(f.stickers_used || 0),
        boxes_used: parseInt(f.boxes_used || 0),
      });
      toast.success(`Saved · ${f.pcs} pcs packed`);
      setF({ date: today(), product_id: "", pcs: "", labels_used: "", stickers_used: "", boxes_used: "", notes: "" });
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form onSubmit={submit} className="space-y-5" data-testid="ops-packing-form">
        <div>
          <BigLabel>Date</BigLabel>
          <BigInput type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        </div>
        <div>
          <BigLabel>Product</BigLabel>
          <BigSelect required value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} data-testid="ops-packing-product">
            <option value="">Choose a product…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.style_name} · {p.size} · {p.color}</option>
            ))}
          </BigSelect>
        </div>
        <div>
          <BigLabel>Pieces Packed</BigLabel>
          <BigInput type="number" inputMode="numeric" required value={f.pcs} onChange={(e) => setDefault(e.target.value)} data-testid="ops-packing-pcs" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <BigLabel>Labels</BigLabel>
            <BigInput type="number" inputMode="numeric" value={f.labels_used} onChange={(e) => setF({ ...f, labels_used: e.target.value })} />
          </div>
          <div>
            <BigLabel>Stickers</BigLabel>
            <BigInput type="number" inputMode="numeric" value={f.stickers_used} onChange={(e) => setF({ ...f, stickers_used: e.target.value })} />
          </div>
          <div>
            <BigLabel>Boxes</BigLabel>
            <BigInput type="number" inputMode="numeric" value={f.boxes_used} onChange={(e) => setF({ ...f, boxes_used: e.target.value })} />
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="ops-packing-submit"
          className="w-full h-16 bg-foreground text-background font-mono text-sm tracking-[0.2em] uppercase font-bold hover:-translate-y-[1px] transition-transform disabled:opacity-50"
        >
          {saving ? "Saving…" : "✓ Save Packing"}
        </button>
      </form>
      <TodayList
        testid="ops-packing-today"
        rows={rows}
        emptyText="No packing yet today"
        columns={(r) => (
          <div className="flex justify-between items-baseline gap-2">
            <span className="truncate">{r.product_label}</span>
            <span className="font-mono tabular whitespace-nowrap">
              <b>{r.pcs}</b> pcs · {r.boxes_used || 0} bx
            </span>
          </div>
        )}
      />
    </>
  );
}

function DispatchOps({ products, parties }) {
  const customers = parties.filter((p) => p.type === "customer");
  const [f, setF] = useState({ date: today(), customer_id: "", product_id: "", boxes: "", vehicle_no: "", transporter_name: "" });
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [stock, setStock] = useState([]);

  const reload = () => {
    api.get("/dispatch").then((r) => setRows(r.data)).catch(() => {});
    api.get("/stock").then((r) => setStock(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  const selectedProduct = products.find((p) => p.id === f.product_id);
  const ppb = selectedProduct?.pieces_per_box || 0;
  const stkRow = stock.find((s) => s.id === f.product_id) || {};
  const stkBoxes = stkRow.stock_boxes || 0;
  const stkPcs = stkRow.stock_pcs || 0;
  const pcs = (Number(f.boxes) || 0) * ppb;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_id) return toast.error("Select a customer");
    if (!f.product_id) return toast.error("Select a product");
    if (!f.boxes || Number(f.boxes) <= 0) return toast.error("Enter boxes");
    if (ppb <= 0) return toast.error("Product has no pcs/box set");
    setSaving(true);
    try {
      await api.post("/dispatch", {
        date: f.date, customer_id: f.customer_id,
        lines: [{ product_id: f.product_id, boxes: parseInt(f.boxes), rate: 0 }],
        vehicle_no: f.vehicle_no, transporter_name: f.transporter_name,
      });
      toast.success(`Dispatched · ${f.boxes} box(es) = ${pcs} pcs`);
      setF({ date: today(), customer_id: "", product_id: "", boxes: "", vehicle_no: f.vehicle_no, transporter_name: f.transporter_name });
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form onSubmit={submit} className="space-y-5" data-testid="ops-dispatch-form">
        <div>
          <BigLabel>Date</BigLabel>
          <BigInput type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        </div>
        <div>
          <BigLabel>Customer</BigLabel>
          <BigSelect required value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} data-testid="ops-dispatch-customer">
            <option value="">Choose a customer…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </BigSelect>
        </div>
        <div>
          <BigLabel>Product</BigLabel>
          <BigSelect required value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value, boxes: "" })} data-testid="ops-dispatch-product">
            <option value="">Choose a product…</option>
            {products.map((p) => {
              const st = stock.find((s) => s.id === p.id) || {};
              const sb = st.stock_boxes || 0;
              const sp = st.stock_pcs || 0;
              return (
                <option key={p.id} value={p.id}>
                  {p.style_name} · {p.size} · {p.color} · {sb}bx / {sp}pcs
                </option>
              );
            })}
          </BigSelect>
        </div>
        {f.product_id && (
          <div className="bg-secondary border-2 border-border p-3 font-mono text-sm tabular">
            Available: <b>{stkBoxes}</b> box{stkBoxes === 1 ? "" : "es"} · {stkPcs} pcs
            {ppb > 0 ? <> · <b>{ppb}</b> pcs/box</> : <span className="text-destructive"> · no pcs/box set</span>}
          </div>
        )}
        <div>
          <BigLabel>Boxes to Dispatch</BigLabel>
          <BigInput type="number" inputMode="numeric" min="1" required value={f.boxes} onChange={(e) => setF({ ...f, boxes: e.target.value })} data-testid="ops-dispatch-boxes" disabled={!f.product_id || ppb === 0} />
          {pcs > 0 && (
            <div className="mt-2 font-mono text-sm tabular">= <b>{pcs}</b> pcs</div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <BigLabel>Vehicle #</BigLabel>
            <BigInput value={f.vehicle_no} onChange={(e) => setF({ ...f, vehicle_no: e.target.value.toUpperCase() })} data-testid="ops-dispatch-vehicle" />
          </div>
          <div>
            <BigLabel>Transporter</BigLabel>
            <BigInput value={f.transporter_name} onChange={(e) => setF({ ...f, transporter_name: e.target.value })} data-testid="ops-dispatch-transporter" />
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="ops-dispatch-submit"
          className="w-full h-16 bg-foreground text-background font-mono text-sm tracking-[0.2em] uppercase font-bold hover:-translate-y-[1px] transition-transform disabled:opacity-50"
        >
          {saving ? "Saving…" : "✓ Dispatch"}
        </button>
      </form>
      <TodayList
        testid="ops-dispatch-today"
        rows={rows}
        emptyText="No dispatches yet today"
        columns={(r) => (
          <div className="flex justify-between items-baseline gap-2">
            <span className="truncate">{r.customer_name} · {r.product_label}</span>
            <span className="font-mono tabular whitespace-nowrap">
              <b>{r.total_boxes ?? r.boxes ?? 0}</b>bx · {r.total_pcs ?? r.pcs ?? 0}pcs
            </span>
          </div>
        )}
      />
    </>
  );
}
